import { useParams, Link } from "react-router-dom";
import { generateDynamicContent } from "@/utils/contentMapping";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Calendar, Clock, User } from "lucide-react";
import SEOMetadata from "@/components/SEOMetadata";
import { isDomain } from "@/hooks/useDomain";

const DynamicBlogPost = () => {
  const { slug } = useParams<{ slug: string }>();
  
  if (!slug) {
    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Post Not Found</h1>
          <Link to={isDomain('news') ? '/' : '/blog'}>
            <Button variant="outline">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to {isDomain('news') ? 'News' : 'Blog'}
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const blogPost = generateDynamicContent(slug);
  const canonicalUrl = isDomain('news') 
    ? `https://news.nexx.net/${slug}` 
    : `https://nexx.net/blog/${slug}`;

  return (
    <>
      <SEOMetadata 
        title={`${blogPost.title} | NEXX`}
        description={blogPost.description}
        keywords={blogPost.keywords.join(', ')}
        canonical={canonicalUrl}
        ogTitle={blogPost.title}
        ogDescription={blogPost.description}
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": blogPost.title,
          "description": blogPost.description,
          "author": {
            "@type": "Organization",
            "name": "NEXX"
          },
          "publisher": {
            "@type": "Organization",
            "name": "NEXX",
            "url": "https://nexx.net"
          },
          "datePublished": new Date().toISOString(),
          "dateModified": new Date().toISOString(),
          "url": canonicalUrl,
          "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": canonicalUrl
          }
        }}
      />
      
      <div className="min-h-screen bg-background">
        {/* Header */}
        <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
          <div className="container mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <Link to={isDomain('news') ? '/' : '/blog'} className="flex items-center space-x-2 hover:opacity-80 transition-opacity">
                <ArrowLeft className="h-5 w-5" />
                <span className="font-medium">Back to {isDomain('news') ? 'News' : 'Blog'}</span>
              </Link>
              
              <div className="flex items-center space-x-4">
                <Link to="https://nexx.net" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                  Visit Main Site
                </Link>
              </div>
            </div>
          </div>
        </header>

        {/* Article */}
        <article className="container mx-auto px-4 py-12 max-w-4xl">
          {/* Article Header */}
          <div className="mb-12">
            <div className="mb-6">
              <span className="inline-block px-3 py-1 text-xs font-medium bg-primary/10 text-primary rounded-full mb-4">
                {blogPost.category}
              </span>
              <h1 className="text-4xl md:text-5xl font-bold mb-6 text-foreground leading-tight">
                {blogPost.title}
              </h1>
              <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
                {blogPost.description}
              </p>
            </div>

            {/* Article Meta */}
            <div className="flex flex-wrap items-center gap-6 text-sm text-muted-foreground border-t border-b border-border py-4">
              <div className="flex items-center gap-2">
                <User className="h-4 w-4" />
                <span>{blogPost.author}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <span>{blogPost.date}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                <span>{blogPost.readTime}</span>
              </div>
            </div>
          </div>

          {/* Article Content */}
          <div 
            className="prose prose-lg max-w-none text-foreground"
            dangerouslySetInnerHTML={{ __html: blogPost.content }}
          />

          {/* Related Services */}
          {blogPost.relatedServices.length > 0 && (
            <div className="mt-12 p-8 bg-muted rounded-lg">
              <h3 className="text-xl font-semibold mb-4 text-foreground">Explore Related Services</h3>
              <div className="flex flex-wrap gap-3">
                {blogPost.relatedServices.map((service, index) => (
                  <Link
                    key={index}
                    to={getServiceLink(service)}
                    className="inline-block px-4 py-2 bg-primary/10 hover:bg-primary/20 text-primary rounded-lg transition-colors text-sm font-medium"
                  >
                    {service}
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* CTA Section */}
          <div className="mt-16 text-center">
            <div className="bg-gradient-to-r from-primary/10 to-primary/5 rounded-2xl p-8">
              <h3 className="text-2xl font-bold mb-4 text-foreground">Ready to Get Started?</h3>
              <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
                Contact our team today to discuss your infrastructure needs and discover how NEXX can help optimize your business operations.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="https://nexx.net/consultation">
                  <Button size="lg" className="w-full sm:w-auto">
                    Schedule a Consultation
                  </Button>
                </Link>
                <Link to="https://nexx.net">
                  <Button variant="outline" size="lg" className="w-full sm:w-auto">
                    Explore Our Services
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </article>
      </div>
    </>
  );
};

const getServiceLink = (service: string): string => {
  const serviceMap: Record<string, string> = {
    'Baremetal Servers': '/baremetal-servers',
    'VPS Servers': '/vps-servers',
    'Residential Proxies': '/residential-proxies',
    'Residential Remote Desktops': '/residential-remote-desktops',
    'Mobile Solutions': '/mobile-solutions',
    'Consultation': '/consultation',
    'Data Center': '/ashburn-interconnect',
    'Colocation': '/baremetal-servers',
  };
  
  return serviceMap[service] || '/consultation';
};

export default DynamicBlogPost;